/**
 * Alisha Chat Store
 *
 * Zustand store for Alisha chat conversation state.
 * Persists conversation across page navigation.
 *
 * IMPORTANT: All Alisha communications route through this store so they can be:
 * 1. Visible in the Exchange page (as the "Alisha" channel)
 * 2. Audited for compliance
 * 3. Used by AlishaFloater from any page
 * 4. Synced in real-time via WebSocket
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { wsService } from '@/services/websocket';
import { alishaAPI } from '@/services/api';

export interface ChatMessage {
  id: string;
  sender: 'iris' | 'user';
  text: string;
  time: string;
}

/** WebSocket exchange message format */
interface ExchangeWSMessage {
  id: string;
  type: 'human_to_iris' | 'iris_to_human' | 'iris_to_operator' | 'operator_to_iris';
  senderId: string;
  senderName: string;
  recipientId: string;
  recipientName: string;
  content: string;
  metadata?: Record<string, unknown>;
  timestamp: string | Date;
  channelId?: string;
}

interface IrisChatStore {
  messages: ChatMessage[];
  isExpanded: boolean;
  isLoading: boolean;
  isWebSocketConnected: boolean;

  /** Send message to Alisha via API and add to conversation */
  sendMessage: (text: string) => Promise<void>;

  /** Add an Alisha response (used internally) */
  addIrisResponse: (text: string) => void;

  /** Add a user message (used for syncing from Exchange) */
  addUserMessage: (text: string) => void;

  /** Toggle chat expanded state */
  toggleExpanded: () => void;

  /** Set chat expanded state */
  setExpanded: (expanded: boolean) => void;

  /** Clear chat history */
  clearHistory: () => void;

  /** Subscribe to WebSocket for real-time updates */
  subscribeToWebSocket: () => () => void;

  /** Handle incoming WebSocket message */
  handleWebSocketMessage: (data: unknown) => void;

  /** Hydrate conversation from backend */
  hydrateFromBackend: () => Promise<void>;
}

const DEFAULT_GREETING: ChatMessage = {
  id: 'iris-greeting',
  sender: 'iris',
  text: "Hi! I'm here if you need anything. Feel free to ask questions or share feedback.",
  time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
};

const formatTime = () =>
  new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

// Track message IDs we've already processed to prevent duplicates
const processedMessageIds = new Set<string>();

// Debounced backend save (2s delay)
let saveTimer: ReturnType<typeof setTimeout> | null = null;
function debouncedSaveToBackend(messages: ChatMessage[]) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    // Strip the greeting message before saving
    const toSave = messages
      .filter(m => m.id !== 'iris-greeting')
      .map(m => ({ id: m.id, sender: m.sender, text: m.text, time: m.time }));
    if (toSave.length > 0) {
      alishaAPI.saveConversation(toSave).catch(() => { /* best-effort */ });
    }
  }, 2000);
}

export const useAlishaChatStore = create<IrisChatStore>()(
  persist(
    (set, get) => ({
  messages: [DEFAULT_GREETING],
  isExpanded: false,
  isLoading: false,
  isWebSocketConnected: false,

  sendMessage: async (text: string) => {
    const trimmedText = text.trim();
    if (!trimmedText || get().isLoading) return;

    // Generate a temporary ID for the user message
    const tempUserId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    // Add user message immediately (will be deduplicated if WebSocket comes back)
    const userMessage: ChatMessage = {
      id: tempUserId,
      sender: 'user',
      text: trimmedText,
      time: formatTime(),
    };

    // Mark as processed so WebSocket doesn't duplicate
    processedMessageIds.add(tempUserId);

    set((state) => ({
      messages: [...state.messages, userMessage],
      isLoading: true,
    }));

    // Build conversation history for context (skip greeting)
    const conversationHistory = get().messages
      .filter(m => m.id !== 'iris-greeting')
      .map(m => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.text,
      }));

    // Call authenticated Alisha API (Claude + Lucian layer)
    try {
      const response = await alishaAPI.chat(trimmedText, conversationHistory);

      // If WebSocket is connected, response may come via WebSocket
      // Add it directly as well (deduplication handles overlaps)
      const irisMessage: ChatMessage = {
        id: `iris-${Date.now()}`,
        sender: 'iris',
        text: response.message,
        time: formatTime(),
      };

      processedMessageIds.add(irisMessage.id);

      set((state) => {
        const updated = [...state.messages, irisMessage];
        debouncedSaveToBackend(updated);
        return { messages: updated, isLoading: false };
      });

      // Trigger immediate credits badge refresh
      window.dispatchEvent(new CustomEvent('credits-refresh'));
    } catch (error) {
      console.error('❌ Alisha chat error:', error);

      // Extract error message for display
      let errorText = 'I encountered an issue. Please try again.';
      if (error instanceof Error) {
        // Check for specific error types
        if (error.message.includes('quota') || error.message.includes('limit')) {
          errorText = 'You have reached your message limit for this month. Please upgrade to continue chatting.';
        } else if (error.message.includes('RATE_LIMIT')) {
          errorText = 'Too many requests. Please wait a moment and try again.';
        } else if (error.message.includes('UNAUTHORIZED')) {
          errorText = 'Your session has expired. Please log in again.';
        } else {
          errorText = `Error: ${error.message}`;
        }
      }

      const errorMessage: ChatMessage = {
        id: `iris-error-${Date.now()}`,
        sender: 'iris',
        text: errorText,
        time: formatTime(),
      };

      set((state) => ({
        messages: [...state.messages, errorMessage],
        isLoading: false,
      }));
    }
  },

  addIrisResponse: (text: string) => {
    const newMessage: ChatMessage = {
      id: `iris-${Date.now()}`,
      sender: 'iris',
      text,
      time: formatTime(),
    };

    set((state) => ({
      messages: [...state.messages, newMessage],
    }));
  },

  addUserMessage: (text: string) => {
    const newMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: text.trim(),
      time: formatTime(),
    };

    set((state) => ({
      messages: [...state.messages, newMessage],
    }));
  },

  toggleExpanded: () => {
    set((state) => ({ isExpanded: !state.isExpanded }));
  },

  setExpanded: (expanded: boolean) => {
    set({ isExpanded: expanded });
  },

  clearHistory: () => {
    processedMessageIds.clear();
    set({ messages: [DEFAULT_GREETING], isLoading: false });
  },

  subscribeToWebSocket: () => {
    // Connect WebSocket if not already connected
    if (!wsService.isConnected) {
      wsService.connect();
    }

    // Subscribe to the Alisha exchange channel
    const unsubscribe = wsService.subscribeExchangeChannel('iris', get().handleWebSocketMessage);
    set({ isWebSocketConnected: true });

    // Return cleanup function
    return () => {
      unsubscribe();
      set({ isWebSocketConnected: false });
    };
  },

  handleWebSocketMessage: (data: unknown) => {
    const msg = data as ExchangeWSMessage;

    // Skip if we've already processed this message
    if (processedMessageIds.has(msg.id)) {
      return;
    }

    // Mark as processed
    processedMessageIds.add(msg.id);

    // Only handle Alisha channel messages
    if (msg.type === 'human_to_iris') {
      // User message - check if it's from another session/tab
      // For now, we skip user messages from WebSocket since we add them locally
      // This prevents duplicates when the same user sends a message
      return;
    }

    if (msg.type === 'iris_to_human') {
      // Alisha response - add to messages
      const timestamp = typeof msg.timestamp === 'string'
        ? new Date(msg.timestamp)
        : msg.timestamp;

      const irisMessage: ChatMessage = {
        id: msg.id,
        sender: 'iris',
        text: msg.content,
        time: timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      };

      set((state) => ({
        messages: [...state.messages, irisMessage],
        isLoading: false,
      }));
    }
  },

  hydrateFromBackend: async () => {
    try {
      const { messages: saved } = await alishaAPI.getConversation();

      // Check if we need to backfill onboarding conversation
      if (!saved || saved.length === 0) {
        console.log('[Alisha Chat] No saved conversation, checking for onboarding backfill...');

        try {
          // Import onboarding API dynamically to avoid circular dependency
          const { onboardingAPI } = await import('../services/api');
          const onboardingState = await onboardingAPI.getState();

          // If onboarding is complete, try to transfer conversation
          if (onboardingState.isComplete) {
            console.log('[Alisha Chat] Onboarding complete, attempting conversation backfill...');
            const convData = await onboardingAPI.getConversation();

            if (convData.conversation?.conversationHistory && convData.conversation.conversationHistory.length > 0) {
              const formatTime = (timestamp?: string) => {
                const date = timestamp ? new Date(timestamp) : new Date();
                return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
              };

              const transferredMessages: Array<{ id: string; sender: string; text: string; time: string }> =
                convData.conversation.conversationHistory.map((msg, index) => ({
                  id: `onboarding-${index}-${Date.now()}`,
                  sender: msg.role === 'user' ? 'user' : 'iris',
                  text: msg.content,
                  time: formatTime(convData.conversation!.lastUpdated),
                }));

              // Save to backend
              await alishaAPI.saveConversation(transferredMessages);
              console.log(`[Alisha Chat] ✅ Backfilled ${transferredMessages.length} messages from onboarding`);

              // Load into store
              const hydrated: ChatMessage[] = [
                DEFAULT_GREETING,
                ...transferredMessages.map(m => ({
                  id: m.id,
                  sender: m.sender as 'iris' | 'user',
                  text: m.text,
                  time: m.time,
                })),
              ];
              set({ messages: hydrated });
              return;
            }
          }
        } catch (backfillErr) {
          console.warn('[Alisha Chat] Backfill failed, continuing normally:', backfillErr);
        }
      }

      // Normal flow - load saved conversation
      if (saved?.length > 0) {
        const hydrated: ChatMessage[] = [
          DEFAULT_GREETING,
          ...saved.map(m => ({
            id: m.id,
            sender: m.sender as 'iris' | 'user',
            text: m.text,
            time: m.time,
          })),
        ];
        set({ messages: hydrated });
      }
    } catch {
      // Fall through to localStorage
    }
  },
}),
    {
      name: 'aerware-iris-chat',
      partialize: (state: IrisChatStore) => ({ messages: state.messages }),
    }
  )
);
